// frontend/src/components/staff/CancelShiftModal.js
//
// Workforce — SCHED-SLICE1-C cancel-with-reason confirmation modal for
// PUBLISHED shifts.
//
// Visual spec: docs/design/workforce-scheduling-slice-1-design.md §6.4
//              (delete-published-shift confirm sub-modal) + visual DoD §18.4.
// Story:       docs/backlog/SCHED-SLICE1-C-publish-workflow-and-audit-hooks.md
//              §AC Scenarios 7, 8, 9, 14.
//
// Triggered by: EditShiftModal's delete affordance when the shift is in
// `status === 'published'` (drafts continue to use the DELETE path).
//
// Contract (BE: workforceShiftsController.cancelShift):
//   - body MUST carry a non-empty trimmed `reason` string
//   - 200  -> { shift: { ..., status: 'cancelled', cancelledAt } }
//   - 400  -> { code: 'MISSING_REASON' }
//   - 400  -> { code: 'SHIFT_ALREADY_CANCELLED' }    (terminal state)
//   - 400  -> { code: 'SHIFT_NOT_PUBLISHED' }        (FE should never hit
//             this — the modal is only opened from a published-shift entry
//             point — but the modal surfaces it as an error toast anyway.)
//
// Visual-DoD §6.4: the confirm button is GOLD (per `_workforce-design-
// system.md` §4 — destructiveness is carried by the copy + heading, not
// by red on the primary CTA). Reason input is REQUIRED with a max length
// of 240 chars to match the shared shift-notes pattern.

import React from "react";
import "./CancelShiftModal.css";

const MAX_REASON_LENGTH = 240;

const CancelShiftModal = ({
  open,
  shift,                 // { _id, startTime, endTime, staffName?, roleLabel? } — display-only context
  staffDisplayName,      // "Jamie Smith" or "Unknown staff"
  onConfirm,             // ({ reason }) => Promise<void>
  onCancel,
  isSubmitting,
  errorMessage,
}) => {
  const [reason, setReason] = React.useState("");
  const [touched, setTouched] = React.useState(false);

  // Reset form on each fresh open.
  React.useEffect(() => {
    if (open) {
      setReason("");
      setTouched(false);
    }
  }, [open, shift && shift._id]);

  // Esc closes (a11y) unless mid-submit.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && typeof onCancel === "function" && !isSubmitting) {
        onCancel();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel, isSubmitting]);

  if (!open) return null;

  const trimmedReason = reason.trim();
  const reasonInvalid = touched && trimmedReason.length === 0;
  const confirmDisabled = isSubmitting || trimmedReason.length === 0;

  const handleConfirm = () => {
    setTouched(true);
    if (trimmedReason.length === 0) return;
    if (typeof onConfirm === "function") {
      onConfirm({ reason: trimmedReason });
    }
  };

  const renderShiftSummary = () => {
    if (!shift) return null;
    const parts = [];
    if (staffDisplayName) parts.push(staffDisplayName);
    if (shift.startTime) {
      try {
        // Render in browser locale; the studio-tz formatting was already
        // applied at fetch time on `shift.startTime`. Defensive try/catch
        // so a malformed ISO never crashes the modal.
        const d = new Date(shift.startTime);
        if (!isNaN(d.getTime())) {
          parts.push(
            d.toLocaleString(undefined, {
              weekday: "short",
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })
          );
        }
      } catch (_e) {
        /* ignore */
      }
    }
    if (shift.roleLabel) parts.push(shift.roleLabel);
    return parts.length > 0 ? parts.join(" — ") : null;
  };

  const shiftSummary = renderShiftSummary();

  return (
    <>
      <div
        className="cancel-shift-modal__backdrop"
        onClick={isSubmitting ? undefined : onCancel}
        aria-hidden="true"
        data-testid="cancel-shift-modal-backdrop"
      />
      <div
        className="cancel-shift-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-shift-modal-heading"
        data-testid="cancel-shift-modal"
      >
        <header className="cancel-shift-modal__header">
          <h2
            id="cancel-shift-modal-heading"
            className="cancel-shift-modal__heading"
          >
            Delete this published shift?
          </h2>
          <button
            type="button"
            className="cancel-shift-modal__close"
            onClick={onCancel}
            disabled={isSubmitting}
            aria-label="Close cancel-shift modal"
            data-testid="cancel-shift-modal-close"
          >
            &times;
          </button>
        </header>

        <div className="cancel-shift-modal__body">
          {errorMessage && (
            <div
              className="cancel-shift-modal__error"
              role="alert"
              data-testid="cancel-shift-modal-error"
            >
              {errorMessage}
            </div>
          )}

          {shiftSummary && (
            <p
              className="cancel-shift-modal__shift-summary"
              data-testid="cancel-shift-modal-shift-summary"
            >
              {shiftSummary}
            </p>
          )}

          <ul className="cancel-shift-modal__impact">
            <li>Remove the shift from the published schedule.</li>
            <li>
              Notify {staffDisplayName || "the affected staff member"}.
            </li>
            <li>Record an audit entry.</li>
          </ul>

          <label
            className="cancel-shift-modal__label"
            htmlFor="cancel-shift-modal-reason"
          >
            Reason
            <span className="cancel-shift-modal__required" aria-hidden="true"> *</span>
          </label>
          <textarea
            id="cancel-shift-modal-reason"
            className="cancel-shift-modal__textarea"
            rows={3}
            maxLength={MAX_REASON_LENGTH}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            disabled={isSubmitting}
            placeholder="Why is this shift being cancelled?"
            aria-required="true"
            aria-invalid={reasonInvalid ? "true" : "false"}
            aria-describedby="cancel-shift-modal-reason-helper"
            data-testid="cancel-shift-modal-reason"
          />
          <p
            id="cancel-shift-modal-reason-helper"
            className={
              reasonInvalid
                ? "cancel-shift-modal__helper cancel-shift-modal__helper--error"
                : "cancel-shift-modal__helper"
            }
            data-testid="cancel-shift-modal-reason-helper"
          >
            {reasonInvalid
              ? "Reason is required."
              : `${trimmedReason.length}/${MAX_REASON_LENGTH} characters`}
          </p>
        </div>

        <footer className="cancel-shift-modal__footer">
          <button
            type="button"
            className="cancel-shift-modal__cancel"
            onClick={onCancel}
            disabled={isSubmitting}
            data-testid="cancel-shift-modal-cancel"
          >
            Keep shift
          </button>
          <button
            type="button"
            className="cancel-shift-modal__confirm"
            onClick={handleConfirm}
            disabled={confirmDisabled}
            data-testid="cancel-shift-modal-confirm"
          >
            {isSubmitting ? "Deleting..." : "Delete"}
          </button>
        </footer>
      </div>
    </>
  );
};

export default CancelShiftModal;
